import { useMemo, useState } from 'react'
import localStorage from 'local-storage'
import { getElapsedTime } from 'date'
import { constants, processError } from 'helpers'
import { useFt } from 'hooks'
import { useSubscription } from 'modules/subscription'
import { useSetPopupShown, useShownPopups, useUser } from 'modules/user'

import useOrderItems from 'compositions/orderItems/util/useOrderItems'


const useCartItems = (cart: CartModule.Cart) => {
  const orderItems = useOrderItems(cart)

  const isGiftSubscriptionOfferEnabled = useFt(constants.features.giftSubscriptionOffer)

  const { subscription } = useSubscription()
  const { shownPopups } = useShownPopups()
  const { isLoggedIn } = useUser()
  const setPopupShown = useSetPopupShown()

  const subscriptionBannerWasShownInSession = localStorage.getSessionItem(constants.localStorageNames.cartSubscriptionBanner)
  const [ courseSubscriptionBannerWasShown, setCourseSubscriptionBannerWasShown ] = useState(false)
  const [ giftSubscriptionBannerWasShown, setGiftSubscriptionBannerWasShown ] = useState(false)

  const offerSubscription = Boolean(subscription?.hasNeverSubscribed && !orderItems.subscription)
  const isSubscriptionBannerEnabled = offerSubscription && (isGiftSubscriptionOfferEnabled ? !orderItems.giftSubscriptionOffer : true)
  const isGiftSubscriptionBannerEnabled = offerSubscription && (isGiftSubscriptionOfferEnabled ? orderItems.giftSubscriptionOffer : false)
  const hasAcademyCourse = orderItems.products.some((product) => product.type === 'AcademyCourse')

  const giftSubscriptionBanner = useMemo(() => {
    if (!isGiftSubscriptionBannerEnabled || giftSubscriptionBannerWasShown) {
      return null
    }

    return {
      onCancel: () => {
        setGiftSubscriptionBannerWasShown(true)
      },
    }
  }, [ isGiftSubscriptionBannerEnabled, giftSubscriptionBannerWasShown ])

  const subscriptionBanner = useMemo(() => {
    if (!isSubscriptionBannerEnabled) {
      return null
    }

    if (hasAcademyCourse) {
      if (courseSubscriptionBannerWasShown) {
        return null
      }

      return {
        onCancel: () => {
          setCourseSubscriptionBannerWasShown(true)
        },
      }
    }

    if (subscriptionBannerWasShownInSession) {
      return null
    }

    const lastUpdated = shownPopups['cartSubscriptionBanner']?.lastUpdated

    // hide popup for 24 hours
    if (lastUpdated && getElapsedTime(lastUpdated, 'hours') < 24) {
      return null
    }

    return {
      onCancel: async () => {
        try {
          if (isLoggedIn) {
            await setPopupShown('cartSubscriptionBanner')
          }
          else {
            localStorage.setSessionItem(constants.localStorageNames.cartSubscriptionBanner, true)
          }
        }
        catch (error) {
          processError(error)
        }
      },
    }
  }, [
    isLoggedIn,
    setPopupShown,
    isSubscriptionBannerEnabled,
    hasAcademyCourse,
    subscriptionBannerWasShownInSession,
    shownPopups,
    courseSubscriptionBannerWasShown,
  ])

  return {
    orderItems,
    giftSubscriptionBanner,
    subscriptionBanner,
    hasAcademyCourse,
  }
}


export default useCartItems
